// UID: BWR-FIN_STMT_1Y-FILE
// @see https://sunstonecreditinc.atlassian.net/browse/SUNENG-2347
// @see https://sunstonecreditinc.atlassian.net/browse/SUNENG-2247

import { ApplicationFormValues } from '../../../../LoanDetailTypes';
import { Ruleset } from '../../interfaces/Ruleset';
import {
  HasApplicantFiledTR,
  IsBetweenMidAprilMidMay,
  IsBetweenMidAugMidOct,
  IsBetweenMidMayMidAug,
  RulesetResult,
  IsExpressLoan,
  IsForProfit,
  IsBetweenMidOctMidNov,
  IsBetweenMidNovAndDec,
  IsBusinessEstablishedMoreThan,
  HasApplicantNotFiledTRLastYear,
  HasApplicantNotFiledTR,
} from '../rules';
import IsBetweenMidFebMidApril from '../rules/IsBetweenMidFebMidApril';
import IsBusinessEstablishedEqualTo from '../rules/IsBusinessEstablishedEqualTo';
import IsFBO from '../rules/IsFBO';

class FinancialStatementFullYearRuleset implements Ruleset {
  private forProfitResult = (
    data: Partial<ApplicationFormValues>
  ): RulesetResult => {
    const isExpress = IsExpressLoan.evaluate(data);
    const isBetweenMidFebMidApril = IsBetweenMidFebMidApril.evaluate(data);

    const isBetweenMidAprilAndMidOct = [
      IsBetweenMidAprilMidMay,
      IsBetweenMidMayMidAug,
      IsBetweenMidAugMidOct,
    ].some((Is) => Is.evaluate(data));
    const isBetweenMidOctAndDec = [
      IsBetweenMidOctMidNov,
      IsBetweenMidNovAndDec,
    ].some((Is) => Is.evaluate(data));

    const establishedMoreThanZeroYearAgo =
      IsBusinessEstablishedMoreThan.setYears(0).evaluate(data);
    const establishedOneYearAgo =
      IsBusinessEstablishedEqualTo.setYears(1).evaluate(data);
    const establishedTwoYearsAgo =
      IsBusinessEstablishedEqualTo.setYears(2).evaluate(data);

    const hasApplicantNotFiledTRLastYear =
      HasApplicantNotFiledTRLastYear.evaluate(data);

    if (isExpress) {
      // A) Is Express Loan && Between Mid April and Mid Oct && Applicant Not Filed TR Last Year && (Established 1 OR 2 Years Ago)
      if (
        isBetweenMidAprilAndMidOct &&
        hasApplicantNotFiledTRLastYear &&
        (establishedOneYearAgo || establishedTwoYearsAgo)
      )
        return RulesetResult.REQUIRED;
      // B) Is Express Loan && Between Mid Oct and Dec && Applicant Not Filed TR Last Year && Established 1 Year Ago
      if (
        isBetweenMidOctAndDec &&
        hasApplicantNotFiledTRLastYear &&
        establishedOneYearAgo
      )
        return RulesetResult.REQUIRED;
    } else {
      // C) Not Express Loan && Between Mid Feb and Mid April && Established More Than 0 Years Ago
      if (isBetweenMidFebMidApril && establishedMoreThanZeroYearAgo)
        return RulesetResult.REQUIRED;
      // D) Not Express Loan && Between Mid Apr and Mid Oct && Applicant Not Filed TR Last Year && Established More Than 0 Years Ago
      if (
        isBetweenMidAprilAndMidOct &&
        establishedMoreThanZeroYearAgo &&
        hasApplicantNotFiledTRLastYear
      )
        return RulesetResult.REQUIRED;
      // E) Not Express Loan && Between Mid Oct and Dec && Applicant Not Filed TR Last Year && Established 1 Year Ago
      if (
        isBetweenMidOctAndDec &&
        hasApplicantNotFiledTRLastYear &&
        establishedOneYearAgo
      )
        return RulesetResult.REQUIRED;
    }

    return RulesetResult.HIDDEN;
  };

  public evaluate = (data: Partial<ApplicationFormValues>): RulesetResult => {
    if (IsFBO.evaluate(data)) return RulesetResult.REQUIRED;
    const isForProfit = IsForProfit.evaluate(data);
    const hasApplicantNotFiledTRLastYear =
      HasApplicantNotFiledTRLastYear.evaluate(data);
    const hasApplicantFiledTRInThePast = HasApplicantFiledTR.evaluate(data);
    const hasApplicantNotFiledTRInThePast =
      HasApplicantNotFiledTR.evaluate(data);

    const isBetweenMidAprilAndMidOct = [
      IsBetweenMidAprilMidMay,
      IsBetweenMidMayMidAug,
      IsBetweenMidAugMidOct,
    ].some((Is) => Is.evaluate(data));
    const isBetweenMidFebAndMidApril = IsBetweenMidFebMidApril.evaluate(data);
    const isBetweenMidFebAndDec = [
      IsBetweenMidFebMidApril,
      IsBetweenMidAprilMidMay,
      IsBetweenMidMayMidAug,
      IsBetweenMidAugMidOct,
      IsBetweenMidOctMidNov,
      IsBetweenMidNovAndDec,
    ].some((Is) => Is.evaluate(data));

    // If For Profit
    if (isForProfit) return this.forProfitResult(data);

    // If Not For Profit

    // A) Filed TR In the Past && [ (Between Mid April and Mid Oct && Not Filed TR Last Year) || (Between Mid Feb and Mid April) ]
    if (
      hasApplicantFiledTRInThePast &&
      ((isBetweenMidAprilAndMidOct && hasApplicantNotFiledTRLastYear) ||
        isBetweenMidFebAndMidApril)
    ) {
      return RulesetResult.REQUIRED;
    }
    // B) Not Filed TR In the Past && Between Mid Feb and Dec
    if (hasApplicantNotFiledTRInThePast && isBetweenMidFebAndDec) {
      return RulesetResult.REQUIRED;
    }

    return RulesetResult.HIDDEN;
  };
}

export default new FinancialStatementFullYearRuleset();
